import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Star, Loader2, CheckCircle2 } from "lucide-react";
import { reputationApi } from "../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function logoSrc(logoDataUrl) {
  if (!logoDataUrl) return null;
  return logoDataUrl.startsWith("http") || logoDataUrl.startsWith("data:") ? logoDataUrl : `${BACKEND}${logoDataUrl}`;
}

export default function PublicReview() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  const [hoverStars, setHoverStars] = useState(0);
  const [selectedStars, setSelectedStars] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [answerMap, setAnswerMap] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [recorded, setRecorded] = useState(false);

  useEffect(() => {
    reputationApi
      .publicGet(token)
      .then((d) => {
        setData(d);
        if (d.submitted) {
          setRecorded(true);
          setSelectedStars(d.starCount || 0);
        }
      })
      .catch((e) => setError(e?.response?.data?.detail || "This review link is invalid or has expired."))
      .finally(() => setLoading(false));
  }, [token]);

  const handleStarClick = async (n) => {
    if (recorded || submitting) return;
    setSelectedStars(n);
    if (n >= 4) {
      setSubmitting(true);
      try {
        await reputationApi.publicSubmit(token, { starCount: n, answers: [] });
      } catch (_) {
        /* still show the thank-you + redirect even if the write failed */
      }
      setSubmitting(false);
      setRecorded(true);
      if (data?.googleReviewLink) {
        setTimeout(() => { window.location.href = data.googleReviewLink; }, 3000);
      }
    } else {
      setShowForm(true);
    }
  };

  const submitForm = async () => {
    setSubmitting(true);
    try {
      const answers = (data.questions || []).map((q) => ({ question: q.label, answer: answerMap[q.id] || "" }));
      await reputationApi.publicSubmit(token, { starCount: selectedStars, answers });
      setRecorded(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not submit your feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#EEF2FF]">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#EEF2FF] px-4">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8 text-center max-w-sm" data-testid="review-error">
          <p className="text-[14px] text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  const company = data?.company || {};
  const logo = logoSrc(company.logoDataUrl);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#EEF2FF] px-4 py-10" data-testid="public-review-page">
      <div
        className="w-full max-w-[380px] rounded-md border-2 border-indigo-200 shadow-xl text-center px-8 py-10"
        style={{ background: "linear-gradient(160deg,#F8FAFC,#E0E7FF)", fontFamily: "Georgia, 'Times New Roman', serif" }}
        data-testid="review-card"
      >
        {logo ? (
          <img src={logo} alt={company.name} className="h-12 max-w-[200px] object-contain mx-auto mb-4" data-testid="review-company-logo" />
        ) : (
          <div className="text-[20px] font-bold text-slate-900 mb-4" data-testid="review-company-name">{company.name}</div>
        )}

        {recorded && selectedStars >= 4 ? (
          <>
            <p className="text-[15px] text-slate-800 italic leading-relaxed mb-6">
              {company.name} would like to know how we did? Your feedback is extremely important to us.
            </p>
            <div className="flex items-center justify-center gap-1 mb-6" data-testid="review-stars-final">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star key={n} className={`h-8 w-8 ${n <= selectedStars ? "text-amber-400 fill-amber-400" : "text-slate-300"}`} />
              ))}
            </div>
            <p style={{ fontSize: "14px" }} className="text-slate-600" data-testid="review-thanks-message">
              We appreciate your feedback, please leave us a review.
            </p>
          </>
        ) : recorded ? (
          <>
            <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
            <p className="text-[15px] text-slate-800 leading-relaxed" data-testid="review-thanks-low">
              Thank you for your feedback — we've shared it with our team and will follow up soon.
            </p>
          </>
        ) : (
          <>
            <p className="text-[15px] text-slate-800 italic leading-relaxed mb-6">
              {company.name} would like to know how we did? Your feedback is extremely important to us.
            </p>
            <div className="flex items-center justify-center gap-1 mb-2" data-testid="review-stars">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => handleStarClick(n)}
                  onMouseEnter={() => setHoverStars(n)}
                  onMouseLeave={() => setHoverStars(0)}
                  data-testid={`review-star-${n}`}
                  className="p-0.5"
                  aria-label={`${n} star${n > 1 ? "s" : ""}`}
                >
                  <Star className={`h-8 w-8 transition-colors ${n <= (hoverStars || selectedStars) ? "text-amber-400 fill-amber-400" : "text-slate-300"}`} />
                </button>
              ))}
            </div>

            {showForm && (
              <div className="mt-6 text-left space-y-3.5" data-testid="review-feedback-form">
                {(data.questions || []).length === 0 ? (
                  <div className="text-[13px] text-slate-500 text-center">Tell us more below and we'll follow up shortly.</div>
                ) : (
                  data.questions.map((q) => (
                    <div key={q.id}>
                      <label className="block text-[12.5px] font-semibold text-slate-700 mb-1">{q.label}</label>
                      <textarea
                        value={answerMap[q.id] || ""}
                        onChange={(e) => setAnswerMap((m) => ({ ...m, [q.id]: e.target.value }))}
                        data-testid={`review-answer-${q.id}`}
                        className="w-full h-20 px-3 py-2 rounded-md border border-slate-300 text-[13px] bg-white/80 focus:outline-none focus:ring-2 focus:ring-blue-500/30 resize-none"
                      />
                    </div>
                  ))
                )}
                {error && <div className="text-[12.5px] text-rose-600" data-testid="review-submit-error">{error}</div>}
                <button
                  type="button"
                  onClick={submitForm}
                  disabled={submitting}
                  data-testid="review-submit-btn"
                  className="w-full h-10 rounded-md bg-blue-600 hover:bg-blue-700 text-white font-sans font-bold text-[13px] disabled:opacity-60"
                >
                  {submitting ? "Submitting…" : "Submit Feedback"}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
